/**
 * Shared Score Calculation Functions
 *
 * These pure functions can be safely used on both client and server side
 * to ensure calculation consistency across the application.
 */

/**
 * Calculate scratch total from an array of scores
 * @param {Array<number>} scores - Array of individual game scores
 * @returns {number} Total scratch score
 */
export const calculateScratchTotal = (scores) => {
  if (!Array.isArray(scores)) {
    console.warn("calculateScratchTotal: scores must be an array");
    return 0;
  }

  return scores.reduce((sum, score) => {
    const parsedScore = parseInt(score, 10);
    return sum + (isNaN(parsedScore) ? 0 : parsedScore);
  }, 0);
};

/**
 * Parse tournament handicapMax. Empty/null means no pin cap.
 * @param {unknown} value
 * @returns {number | null}
 */
export const parseHandicapMax = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Load handicap rules from a tournaments row. Null handicapMax is no cap.
 * @param {{ handicapBase?: unknown, handicapPercentage?: unknown, handicapMax?: unknown }} row
 */
export const tournamentHandicapRulesFromRow = (row) => {
  if (
    row == null ||
    row.handicapBase === null ||
    row.handicapBase === undefined ||
    row.handicapPercentage === null ||
    row.handicapPercentage === undefined
  ) {
    throw new Error(
      "Tournament handicap rules are not configured. Please set handicap base and percentage."
    );
  }
  const basis = Number(row.handicapBase);
  const percentage = Number(row.handicapPercentage);
  if (!Number.isFinite(basis) || !Number.isFinite(percentage)) {
    throw new Error(
      "Tournament handicap rules are not configured. Please set handicap base and percentage."
    );
  }
  return {
    basis,
    percentage,
    max: parseHandicapMax(row.handicapMax),
  };
};

/**
 * Calculate handicap for a bowler based on their average
 * @param {number} average - Bowler's average
 * @param {number} basis - Handicap basis (e.g., 220)
 * @param {number} percentage - Handicap percentage (e.g., 90)
 * @param {number | null | undefined} max - Pin cap, or null/undefined for no maximum
 * @param {boolean} hasAverage - Whether the bowler has an average (default: true)
 * @returns {number} Calculated handicap
 */
export const calculateHandicap = (
  average,
  basis,
  percentage,
  max,
  hasAverage = true
) => {
  // If bowler has no average, return 0 handicap (bowl scratch)
  if (!hasAverage) {
    return 0;
  }

  const avg = Number(average);
  const base = Number(basis);
  const pct = Number(percentage);
  if (!Number.isFinite(avg) || !Number.isFinite(base) || !Number.isFinite(pct)) {
    console.warn(
      "calculateHandicap: average, basis, and percentage must be numbers"
    );
    return 0;
  }

  const calculatedHandicap = Math.floor((base - avg) * (pct / 100));
  const uncapped = Math.max(calculatedHandicap, 0);
  const cap = parseHandicapMax(max);
  if (cap === null) {
    return uncapped;
  }
  return Math.min(uncapped, cap);
};

/**
 * Calculate handicap total (scratch + handicap for all games)
 * @param {number} scratchTotal - Total scratch score
 * @param {number} handicap - Handicap per game
 * @param {number} gamesPlayed - Number of games played
 * @returns {number} Total handicap score
 */
export const calculateHandicapTotal = (scratchTotal, handicap, gamesPlayed) => {
  if (
    typeof scratchTotal !== "number" ||
    typeof handicap !== "number" ||
    typeof gamesPlayed !== "number"
  ) {
    console.warn("calculateHandicapTotal: All parameters must be numbers");
    return scratchTotal || 0;
  }

  return scratchTotal + handicap * gamesPlayed;
};

/**
 * Calculate high game from an array of scores
 * @param {Array<number>} scores - Array of individual game scores
 * @returns {number} Highest game score
 */
export const calculateHighGame = (scores) => {
  if (!Array.isArray(scores) || scores.length === 0) {
    return 0;
  }

  return Math.max(...scores.map((score) => parseInt(score, 10) || 0));
};

/**
 * Calculate high series (scratch total) from an array of scores
 * @param {Array<number>} scores - Array of individual game scores
 * @returns {number} High series total
 */
export const calculateHighSeries = (scores) => {
  return calculateScratchTotal(scores);
};

/**
 * Validate a single game score
 * @param {number} score - Game score to validate
 * @returns {Object} Validation result with isValid and message
 */
export const validateGameScore = (score) => {
  const parsedScore = parseInt(score, 10);

  if (isNaN(parsedScore)) {
    return { isValid: false, message: "Score must be a valid number" };
  }

  if (parsedScore < 0) {
    return { isValid: false, message: "Score cannot be negative" };
  }

  if (parsedScore > 300) {
    return { isValid: false, message: "Score cannot exceed 300" };
  }

  return { isValid: true, message: "Score is valid" };
};

/**
 * Calculate team totals from bowler totals
 * @param {Array<Object>} bowlerTotals - Array of bowler total objects
 * @returns {Object} Team totals { teamScratchTotal, teamHandicapTotal, hasAnyAverages }
 */
export const calculateTeamTotals = (bowlerTotals) => {
  if (!Array.isArray(bowlerTotals)) {
    console.warn("calculateTeamTotals: bowlerTotals must be an array");
    return { teamScratchTotal: 0, teamHandicapTotal: 0, hasAnyAverages: false };
  }

  return bowlerTotals.reduce(
    (acc, bowler) => {
      acc.teamScratchTotal += bowler.scratchTotal || 0;
      acc.teamHandicapTotal += bowler.handicapTotal || 0;
      // Track if any bowler has an average
      if (bowler.hasAverage !== false) {
        acc.hasAnyAverages = true;
      }
      return acc;
    },
    { teamScratchTotal: 0, teamHandicapTotal: 0, hasAnyAverages: false }
  );
};

/**
 * Calculate bowler totals from scores and handicap data
 * @param {Array<number>} scores - Array of game scores
 * @param {number} average - Bowler's average
 * @param {Object} handicapRules - Handicap calculation rules
 * @param {boolean} hasAverage - Whether the bowler has an average (default: true)
 * @returns {Object} Bowler totals { scratchTotal, handicap, handicapTotal, gamesPlayed, hasAverage }
 */
export const calculateBowlerTotals = (
  scores,
  average,
  handicapRules,
  hasAverage = true
) => {
  const scratchTotal = calculateScratchTotal(scores);
  const gamesPlayed = scores.filter(
    (score) => score !== null && score !== undefined
  ).length;

  let handicap = 0;
  let handicapTotal = scratchTotal;

  const numericAverage =
    typeof average === "number"
      ? average
      : Number(average?.average ?? average);

  if (handicapRules && hasAverage && Number.isFinite(numericAverage)) {
    handicap = calculateHandicap(
      numericAverage,
      handicapRules.basis ?? 220,
      handicapRules.percentage ?? 90,
      handicapRules.max,
      hasAverage
    );
    handicapTotal = calculateHandicapTotal(scratchTotal, handicap, gamesPlayed);
  }

  return {
    scratchTotal,
    handicap,
    handicapTotal,
    gamesPlayed,
    hasAverage,
  };
};
